import { useState, useEffect } from "react";
import { Package, Scroll, Trash2 } from "lucide-react";
import { PageHeader, Empty, btn } from "@/components/Page";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { SKILL_META, RARITY, CLASS_META } from "@/lib/game";

function RarityTag({ rarity }) {
  const r = RARITY[rarity] || RARITY.common;
  return (
    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ${r.text} ${r.border} ${r.bg}`}>
      {r.label}
    </span>
  );
}

function getArmorChestModalDescription(chestType) {
  const rarityLabel =
    chestType === 'armor' ? 'common' :
    chestType === 'uncommon_armor' ? 'uncommon' :
    chestType === 'rare_armor' ? 'rare' :
    chestType === 'elite_armor' ? 'elite' :
    chestType === 'legendary_armor' ? 'legendary' : null;

  return rarityLabel
    ? `Open to reveal 3 random ${rarityLabel} armor or helmet pieces. Choose 1 to keep.`
    : 'Select one of the following items to add to your equipment collection';
}

const CHEST_MELT_CONFIG = {
  common_weapon_chest: { type: "weapon", rarity: "common", kind: "weapon", amount: 25 }, common_armor_chest: { type: "armor", rarity: "common", kind: "armor", amount: 25 },
  uncommon_weapon_chest: { type: "uncommon_weapon", rarity: "uncommon", kind: "weapon", amount: 37 }, uncommon_armor_chest: { type: "uncommon_armor", rarity: "uncommon", kind: "armor", amount: 37 },
  rare_weapon_chest: { type: "rare_weapon", rarity: "rare", kind: "weapon", amount: 50 }, rare_armor_chest: { type: "rare_armor", rarity: "rare", kind: "armor", amount: 50 },
  elite_weapon_chest: { type: "elite_weapon", rarity: "elite", kind: "weapon", amount: 50 }, elite_armor_chest: { type: "elite_armor", rarity: "elite", kind: "armor", amount: 50 },
  legendary_weapon_chest: { type: "legendary_weapon", rarity: "legendary", kind: "weapon", amount: 50 }, legendary_armor_chest: { type: "legendary_armor", rarity: "legendary", kind: "armor", amount: 50 },
};
const getChestMeltConfig = (type) => Object.values(CHEST_MELT_CONFIG).find((chest) => chest.type === type);

export default function Inventory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [consumables, setConsumables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lovePotionModal, setLovePotionModal] = useState(false);
  const [lovePotionTargets, setLovePotionTargets] = useState([]);
  const [selectedLovePotionTarget, setSelectedLovePotionTarget] = useState("");
  const [loadingLovePotionTargets, setLoadingLovePotionTargets] = useState(false);
  
  // Chest opening states
  const [chestModal, setChestModal] = useState(null); // {type: 'weapon'|'armor', items: [...]}
  const [chestConfirm, setChestConfirm] = useState(null); // {type, item}
  const [confirm, setConfirm] = useState(null);

  const getRemainingCooldownDays = (expiresAt) => {
    if (!expiresAt) return 0;
    return Math.max(0, Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24)));
  };

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    try {
      const { data } = await api.get("/inventory");
      setConsumables(data.consumables || []);
    } catch (e) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const openEquipmentChest = async (chestType) => {
    setBusy(true);
    try {
      let endpoint = '';
      if (chestType === 'weapon') endpoint = '/gladiators/open-weapon-chest';
      else if (chestType === 'armor') endpoint = '/gladiators/open-armor-chest';
      else if (chestType === 'uncommon_weapon') endpoint = '/gladiators/open-uncommon-weapon-chest';
      else if (chestType === 'uncommon_armor') endpoint = '/gladiators/open-uncommon-armor-chest';
      else if (chestType === 'rare_weapon') endpoint = '/gladiators/open-rare-weapon-chest';
      else if (chestType === 'rare_armor') endpoint = '/gladiators/open-rare-armor-chest';
      else if (chestType === 'elite_weapon') endpoint = '/gladiators/open-elite-weapon-chest';
      else if (chestType === 'elite_armor') endpoint = '/gladiators/open-elite-armor-chest';
      else if (chestType === 'legendary_weapon') endpoint = '/gladiators/open-legendary-weapon-chest';
      else if (chestType === 'legendary_armor') endpoint = '/gladiators/open-legendary-armor-chest';
      
      const { data } = await api.post(endpoint);
      setChestModal({ type: chestType, items: data.items });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const selectChestItem = async () => {
    if (!chestConfirm) return;
    setBusy(true);
    try {
      const { data } = await api.post('/gladiators/select-chest-item', {
        chest_type: chestConfirm.type,
        item_id: chestConfirm.item.id
      });
      toast.success(data.message, { icon: "📦" });
      setChestConfirm(null);
      setChestModal(null);
      await loadInventory(); // Reload inventory
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const meltChest = async (chestType) => {
    setBusy(true);
    try {
      const { data } = await api.post('/gladiators/melt-chest', { chest_type: chestType });
      toast.success(`Melted chest: received ${data.amount} ${data.rarity} ${data.fragment_kind} fragments.`);
      setConfirm(null);
      setChestModal(null);
      await loadInventory();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openLovePotionModal = async () => {
    setLoadingLovePotionTargets(true);
    try {
      const { data } = await api.get("/vivarium");
      const targets = (data.beasts || []).filter(
        (beast) => !beast.can_breed && beast.breed_cooldown_expires_at && getRemainingCooldownDays(beast.breed_cooldown_expires_at) > 0
      );
      if (targets.length === 0) {
        toast.error("No beasts currently have an active breeding cooldown.");
        return;
      }
      setLovePotionTargets(targets);
      setSelectedLovePotionTarget(targets[0].id);
      setLovePotionModal(true);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoadingLovePotionTargets(false);
    }
  };

  const useLovePotion = async () => {
    if (!selectedLovePotionTarget) return;
    setBusy(true);
    try {
      const { data } = await api.post("/vivarium/beast/love-potion", { beast_id: selectedLovePotionTarget });
      toast.success(data.message, { icon: "💙" });
      setLovePotionModal(false);
      setLovePotionTargets([]);
      setSelectedLovePotionTarget("");
      await loadInventory();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const handleItemClick = (itemId) => {
    if (itemId === 'common_weapon_chest') {
      openEquipmentChest('weapon');
    } else if (itemId === 'common_armor_chest') {
      openEquipmentChest('armor');
    } else if (itemId === 'uncommon_weapon_chest') {
      openEquipmentChest('uncommon_weapon');
    } else if (itemId === 'uncommon_armor_chest') {
      openEquipmentChest('uncommon_armor');
    } else if (itemId === 'rare_weapon_chest') {
      openEquipmentChest('rare_weapon');
    } else if (itemId === 'rare_armor_chest') {
      openEquipmentChest('rare_armor');
    } else if (itemId === 'elite_weapon_chest') {
      openEquipmentChest('elite_weapon');
    } else if (itemId === 'elite_armor_chest') {
      openEquipmentChest('elite_armor');
    } else if (itemId === 'legendary_weapon_chest') {
      openEquipmentChest('legendary_weapon');
    } else if (itemId === 'legendary_armor_chest') {
      openEquipmentChest('legendary_armor');
    } else if (itemId === 'love_potion') {
      openLovePotionModal();
    } else {
      // Other items navigate as before
      if (itemId === 'instant_scout') navigate('/roster?useItem=instant_scout');
      else if (itemId === 'campaign_stamina_potion') navigate('/campaign');
      else if (itemId.startsWith('beast_food_')) navigate('/vivarium');
      else if (itemId === 'beast_supplement') navigate('/vivarium');
      else if (itemId === 'love_potion') navigate('/vivarium');
      else if (itemId === 'experienced_medicus') navigate('/roster?useItem=experienced_medicus');
      else if (itemId === 'recovery_ether') navigate('/roster?useItem=recovery_ether');
      else navigate('/dashboard');
    }
  };

  const getItemIcon = (itemId) => {
    if (itemId === 'instant_scout') return '/instant-scout.png';
    if (itemId === 'campaign_stamina_potion') return '/campaign-stamina-v2.png';
    if (itemId.startsWith('beast_food_')) return '/beast-food-v3.png';
    if (itemId === 'beast_supplement') return '/beast-supplement.png';
    if (itemId === 'love_potion') return '/love-potion.png';
    if (itemId === 'experienced_medicus') return '/medicus.png';
    if (itemId === 'recovery_ether') return '/recovery-ether.png';
    if (itemId === 'common_weapon_chest' || itemId === 'common_armor_chest') return '/common-chest.png';
    if (itemId === 'uncommon_weapon_chest' || itemId === 'uncommon_armor_chest') return '/uncommon-chest.png';
    if (itemId === 'rare_weapon_chest' || itemId === 'rare_armor_chest') return '/rare-chest.png';
    if (itemId === 'elite_weapon_chest' || itemId === 'elite_armor_chest') return '/elite-chest.png';
    if (itemId === 'legendary_weapon_chest' || itemId === 'legendary_armor_chest') return '/legendary-chest.png';
    return '/instant-scout.png';
  };

  if (loading) return <div className="text-center py-20 text-zinc-500">Loading inventory...</div>;

  return (
    <div className="space-y-8">
      <PageHeader
        icon={Package}
        title="Lanista Inventory"
        subtitle="Your collected consumables and special items"
      />

      {consumables.length === 0 ? (
        <Empty label="Your inventory is empty">
          <p className="text-sm text-zinc-400 mb-4">
            Consumable items and special scrolls will appear here when you acquire them.
          </p>
          <button onClick={() => navigate("/dashboard")} className={btn()}>
            Back to Dashboard
          </button>
        </Empty>
      ) : (
        <div>
          <h3 className="font-cinzel text-xl text-amber-400 mb-4 uppercase tracking-wide">Consumables</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {consumables.map((item) => (
              <div
                key={item.id}
                data-testid={`consumable-${item.id}`}
                className="relative border border-amber-700/30 bg-zinc-950 p-4 hover:border-amber-600/50 transition-colors cursor-pointer"
                onClick={() => handleItemClick(item.id)}
                title="Click to use this item"
              >
                {CHEST_MELT_CONFIG[item.id] && (() => { const chest = CHEST_MELT_CONFIG[item.id]; return <button type="button" data-testid={`inventory-melt-${item.id}`} title={`Melt for ${chest.amount} ${chest.rarity} ${chest.kind} fragments`} aria-label={`Melt ${item.name} for fragments`} onClick={(e) => { e.stopPropagation(); setConfirm({ title: `Melt ${item.name}?`, message: `This permanently consumes one unopened ${item.name}. You will receive exactly ${chest.amount} ${chest.rarity} ${chest.kind} fragments. No Denarii, Gold, or other currency is awarded.`, confirmLabel: "Melt for Fragments", onConfirm: () => meltChest(chest.type) }); }} disabled={busy} className="absolute right-2 top-2 p-1.5 text-zinc-500 transition-colors hover:bg-red-950/40 hover:text-red-400 disabled:opacity-50"><Trash2 size={15} /></button>; })()}
                <div className="flex items-center justify-center mb-3">
                  <img src={getItemIcon(item.id)} alt={item.name} className="w-24 h-24 object-contain" />
                </div>
                <h4 className="font-cinzel text-amber-300 text-sm text-center mb-1">{item.name}</h4>
                <p className="text-xs text-zinc-500 text-center mb-3">{item.description}</p>
                <div className="text-center">
                  <span className="px-3 py-1 bg-amber-900/30 border border-amber-700/50 text-amber-300 text-sm font-semibold rounded-sm">
                    x{item.count}
                  </span>
                </div>
                {item.id === 'love_potion' && (
                  <button
                    type="button"
                    data-testid="use-consumable-love_potion-button"
                    className={btn() + " w-full mt-3 text-xs py-2 justify-center"}
                    onClick={(e) => {
                      e.stopPropagation();
                      openLovePotionModal();
                    }}
                    disabled={busy || loadingLovePotionTargets}
                  >
                    Use
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal open={lovePotionModal} onClose={() => { setLovePotionModal(false); setLovePotionTargets([]); setSelectedLovePotionTarget(""); }} title="Use Love Potion">
        <div className="space-y-4" data-testid="love-potion-inventory-modal">
          <div className="border border-amber-900/40 bg-amber-950/10 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <img src="/love-potion.png" alt="Love Potion" className="w-12 h-12 object-contain" />
              <div>
                <div className="font-cinzel text-amber-300 text-sm">Love Potion</div>
                <p className="text-xs text-zinc-400">Choose a beast with an active breeding cooldown. This removes 4 days, or clears the cooldown if less than 4 days remain.</p>
              </div>
            </div>
          </div>

          <div className="space-y-2 max-h-72 overflow-y-auto">
            {lovePotionTargets.map((beast) => {
              const selected = selectedLovePotionTarget === beast.id;
              const remainingDays = getRemainingCooldownDays(beast.breed_cooldown_expires_at);
              return (
                <button
                  key={beast.id}
                  type="button"
                  data-testid={`love-potion-target-${beast.id}`}
                  onClick={() => setSelectedLovePotionTarget(beast.id)}
                  className={`w-full text-left border rounded-sm p-3 transition-colors ${selected ? 'border-amber-500 bg-amber-950/20' : 'border-zinc-800 bg-zinc-900 hover:border-amber-700/60'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-cinzel text-zinc-100">{beast.custom_name || beast.display_name}</div>
                      <div className="text-[11px] text-zinc-500 mt-1 capitalize">{(beast.species || '').replace(/_/g, ' ')} • {beast.tier}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-rose-300">{remainingDays} day{remainingDays === 1 ? '' : 's'} remaining</div>
                      <div className="text-[11px] text-amber-300 mt-1">After potion: {Math.max(0, remainingDays - 4)} day{Math.max(0, remainingDays - 4) === 1 ? '' : 's'}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex gap-2 justify-end">
            <button type="button" className={btn('ghost') + ' flex-1'} onClick={() => { setLovePotionModal(false); setLovePotionTargets([]); setSelectedLovePotionTarget(""); }} disabled={busy}>
              Cancel
            </button>
            <button type="button" data-testid="love-potion-inventory-confirm" className={btn() + ' flex-1'} onClick={useLovePotion} disabled={busy || !selectedLovePotionTarget}>
              Use Love Potion
            </button>
          </div>
        </div>
      </Modal>

      {/* Equipment Chest Opening Modal */}
      {chestModal && (
        <Modal open={!!chestModal} onClose={() => setChestModal(null)} title={
          chestModal.type === 'weapon' ? 'Weapon Chest' :
          chestModal.type === 'armor' ? 'Armor Chest' :
          chestModal.type === 'uncommon_weapon' ? 'Uncommon Weapon Chest' :
          chestModal.type === 'uncommon_armor' ? 'Uncommon Armor Chest' :
          chestModal.type === 'rare_weapon' ? 'Rare Weapon Chest' :
          chestModal.type === 'rare_armor' ? 'Rare Armor Chest' :
          chestModal.type === 'elite_weapon' ? 'Elite Weapon Chest' :
          chestModal.type === 'elite_armor' ? 'Elite Armor Chest' :
          chestModal.type === 'legendary_weapon' ? 'Legendary Weapon Chest' :
          'Legendary Armor Chest'
        }>
          <div className="space-y-4">
            {/* Chest Display */}
            <div className="border border-amber-900/40 bg-gradient-to-br from-amber-950/20 to-orange-950/20 rounded-sm p-4">
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src={
                    chestModal.type.includes('legendary') ? '/legendary-chest.png' :
                    chestModal.type.includes('elite') ? '/elite-chest.png' :
                    chestModal.type.includes('rare') ? '/rare-chest.png' :
                    chestModal.type.includes('uncommon') ? '/uncommon-chest.png' : 
                    '/common-chest.png'
                  } 
                  alt="Equipment Chest" 
                  className="w-16 h-16 object-contain" 
                />
                <div className="flex-1">
                  <div className="text-base font-cinzel text-amber-300 font-semibold">
                    {chestModal.type === 'weapon' ? 'Common Weapon' :
                     chestModal.type === 'armor' ? 'Common Armor' :
                     chestModal.type === 'uncommon_weapon' ? 'Uncommon Weapon' :
                     chestModal.type === 'uncommon_armor' ? 'Uncommon Armor' :
                     chestModal.type === 'rare_weapon' ? 'Rare Weapon' :
                     chestModal.type === 'rare_armor' ? 'Rare Armor' :
                     chestModal.type === 'elite_weapon' ? 'Elite Weapon' :
                     chestModal.type === 'elite_armor' ? 'Elite Armor' :
                     chestModal.type === 'legendary_weapon' ? 'Legendary Weapon' :
                     'Legendary Armor'} Chest
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">Choose 1 item to keep</div>
                </div>
              </div>
              <div className="text-sm font-semibold text-orange-400 border-t border-amber-900/40 pt-3">
                {getArmorChestModalDescription(chestModal.type)}
              </div>
            </div>

            {/* Item Selection */}
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-cinzel">
                Available Items (3)
              </div>
              <div className="space-y-2">
                {chestModal.items.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => setChestConfirm({ type: chestModal.type, item })}
                    disabled={busy}
                    className="w-full text-left p-4 bg-zinc-900/80 hover:bg-amber-950/30 border border-zinc-800 hover:border-amber-600/60 rounded-sm transition-all group disabled:opacity-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="text-sm font-cinzel font-semibold text-zinc-200 group-hover:text-amber-300 transition-colors">
                            {item.name}
                          </div>
                          <RarityTag rarity={item.rarity || 'common'} />
                        </div>
                        
                        {/* Item Stats */}
                        <div className="space-y-1">
                          {item.slot && (
                            <div className="text-xs text-zinc-500 capitalize">
                              {item.slot === 'offhand' ? 'Off-hand' : item.slot}
                            </div>
                          )}
                          {item.classes && item.classes.length > 0 && (
                            <div className="text-xs text-zinc-500">
                              Classes: {item.classes.map(c => CLASS_META[c]?.label || c).join(', ')}
                            </div>
                          )}
                          {item.mods && Object.keys(item.mods).length > 0 && (
                            <div className="flex gap-2 flex-wrap mt-2">
                              {Object.entries(item.mods).map(([stat, value]) => (
                                <span key={stat} className="text-xs text-emerald-400 font-semibold">
                                  +{value} {SKILL_META[stat]?.label || stat}
                                </span>
                              ))}
                            </div>
                          )}
                          {item.affixes && item.affixes.length > 0 && (
                            <div className="flex gap-2 flex-wrap mt-2">
                              {item.affixes.map((affix, idx) => (
                                <span key={idx} className="text-xs text-amber-400 font-semibold">
                                  {affix.display}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-amber-400 group-hover:text-amber-300 uppercase tracking-wide font-cinzel">
                          Select
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Cancel Button */}
            <button
              onClick={() => setChestModal(null)}
              className={btn() + " w-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* Chest Item Confirmation Modal */}
      {chestConfirm && (
        <Modal open={!!chestConfirm} onClose={() => setChestConfirm(null)} title="Confirm Selection">
          <div className="space-y-4">
            {/* Confirmation Message */}
            <div className="text-center py-4">
              <p className="text-sm text-zinc-300 mb-4">
                Add this item to your equipment collection?
              </p>
              
              {/* Item Details */}
              <div className="bg-zinc-900/80 border border-amber-600/40 rounded-sm p-4 text-left">
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-base font-cinzel font-semibold text-amber-300">
                    {chestConfirm.item.name}
                  </div>
                  <RarityTag rarity={chestConfirm.item.rarity || 'common'} />
                </div>
                
                <div className="space-y-2">
                  {chestConfirm.item.slot && (
                    <div className="text-sm text-zinc-400 capitalize">
                      Type: {chestConfirm.item.slot === 'offhand' ? 'Off-hand' : chestConfirm.item.slot}
                    </div>
                  )}
                  {chestConfirm.item.classes && chestConfirm.item.classes.length > 0 && (
                    <div className="text-sm text-zinc-400">
                      Classes: {chestConfirm.item.classes.map(c => CLASS_META[c]?.label || c).join(', ')}
                    </div>
                  )}
                  {chestConfirm.item.mods && Object.keys(chestConfirm.item.mods).length > 0 && (
                    <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-800">
                      {Object.entries(chestConfirm.item.mods).map(([stat, value]) => (
                        <span key={stat} className="text-sm text-emerald-400 font-semibold">
                          +{value} {SKILL_META[stat]?.label || stat}
                        </span>
                      ))}
                    </div>
                  )}
                  {chestConfirm.item.affixes && chestConfirm.item.affixes.length > 0 && (
                    <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-800 mt-2">
                      {chestConfirm.item.affixes.map((affix, idx) => (
                        <span key={idx} className="text-sm text-amber-400 font-semibold">
                          {affix.display}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-amber-950/20 border border-amber-900/40 rounded-sm p-3">
              <p className="text-xs text-amber-300 text-center">
                The other 2 items will be lost. This choice cannot be undone.
              </p>
            </div>
            {getChestMeltConfig(chestConfirm.type) && (() => { const chest = getChestMeltConfig(chestConfirm.type); return <button type="button" data-testid="inventory-pending-chest-melt" onClick={() => setConfirm({ title: "Melt this chest choice?", message: `This permanently consumes the unopened chest and its three generated choices. You will receive exactly ${chest.amount} ${chest.rarity} ${chest.kind} fragments. No currency is awarded.`, confirmLabel: "Melt for Fragments", onConfirm: () => meltChest(chest.type) })} disabled={busy} className="w-full border border-amber-700/60 px-4 py-2.5 text-xs uppercase tracking-widest text-amber-200 transition-colors hover:bg-amber-950/30 disabled:opacity-50">Melt for {chest.amount} {chest.rarity} {chest.kind} fragments</button>; })()}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setChestConfirm(null)}
                className={btn() + " flex-1 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"}
                disabled={busy}
              >
                Back
              </button>
              <button
                onClick={selectChestItem}
                className={btn() + " flex-1 bg-amber-600 hover:bg-amber-500"}
                disabled={busy}
              >
                {busy ? "Adding..." : "Confirm Selection"}
              </button>
            </div>
          </div>
        </Modal>
      )}
      <ConfirmDialog state={confirm} busy={busy} onClose={() => setConfirm(null)} />
    </div>
  );
}
